import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button, Card, CardSubtitle, CardTitle, Combobox, Field, Input } from '@keobook/ui';
import { api, ApiError } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';

type Account = Awaited<ReturnType<typeof api.getAccounts>>['accounts'][number];

/** One line of a journal entry as the user is editing it. Debit and credit are
 *  separate positive amounts — accountants enter into the correct column rather
 *  than remembering a +/- sign convention. */
type Line = {
  accountId: string;
  debit: string;
  credit: string;
  memo: string;
};

const blankLine = (): Line => ({ accountId: '', debit: '', credit: '', memo: '' });

function dollarsToCents(s: string): number | null {
  const cleaned = s.trim().replace(/[$,_\s]/g, '');
  if (cleaned === '' || cleaned === '-') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  // Round to avoid floating-point cents drift.
  return Math.round(n * 100);
}

/** Signed cents for a line — debit positive, credit negative (the API's
 *  convention). null when the line isn't a single positive amount in exactly
 *  one column. */
function lineSignedCents(l: Line): number | null {
  const d = dollarsToCents(l.debit);
  const c = dollarsToCents(l.credit);
  const hasD = d !== null && d > 0;
  const hasC = c !== null && c > 0;
  if (hasD === hasC) return null; // both columns filled, or neither → invalid
  return hasD ? d! : -c!;
}

/** Manual journal entry composer — collapsible, balance-checked client-side. */
export function AddEntryComposer({ onCreated }: { onCreated: () => void }) {
  const fmt = useMoneyFormatter();
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [description, setDescription] = useState('');
  const [postedAt, setPostedAt] = useState<string>(new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState<Line[]>([blankLine(), blankLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void api
      .getAccounts()
      .then((r) => setAccounts([...r.accounts].sort((a, b) => a.code.localeCompare(b.code))))
      .catch(() => undefined);
  }, [open]);

  // Searchable by code OR name — the Combobox substring-matches this label.
  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: `${a.code} — ${a.name}` })),
    [accounts],
  );

  const signed = lines.map(lineSignedCents);
  const sumDebits = lines.reduce((s, l) => {
    const d = dollarsToCents(l.debit);
    return s + (d && d > 0 ? d : 0);
  }, 0);
  const sumCredits = lines.reduce((s, l) => {
    const c = dollarsToCents(l.credit);
    return s + (c && c > 0 ? c : 0);
  }, 0);
  const allLinesValid = signed.every((v) => v !== null);
  const allHaveAccounts = lines.every((l) => l.accountId);
  const balanced = lines.length >= 2 && allLinesValid && sumDebits > 0 && sumDebits === sumCredits;
  const offBy = sumDebits - sumCredits;

  function patchLine(i: number, patch: Partial<Line>) {
    setLines((prev) => prev.map((l, j) => (i === j ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, blankLine()]);
  }
  function removeLine(i: number) {
    setLines((prev) => prev.filter((_, j) => j !== i));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!balanced || !allHaveAccounts || description.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.createJournalEntry({
        description: description.trim(),
        postedAt: new Date(postedAt + 'T12:00:00Z').toISOString(),
        lines: lines.map((l, i) => ({
          accountId: l.accountId,
          amountCents: signed[i]!,
          memo: l.memo.trim() || undefined,
        })),
      });
      // Reset
      setDescription('');
      setLines([blankLine(), blankLine()]);
      setOpen(false);
      onCreated();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const p = err.problem as { title?: string };
        setError(p.title ?? `Create failed (${err.status}).`);
      } else {
        setError(err instanceof Error ? err.message : 'Create failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div style={{ marginBottom: '1.25rem' }}>
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
          + Add a manual entry
        </Button>
      </div>
    );
  }

  const amtInput: React.CSSProperties = { textAlign: 'right', minWidth: 120 };

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <Card>
        <CardTitle>Add a manual journal entry</CardTitle>
        <CardSubtitle>
          Double-entry — total debits must equal total credits. Enter each amount in the Debit or
          Credit column.
        </CardSubtitle>
        {error && (
          <div role="alert" className="kb-form-error" style={{ marginTop: 8 }}>
            {error}
          </div>
        )}
        <form onSubmit={onSubmit} style={{ marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Posted date" required>
              <Input
                type="date"
                value={postedAt}
                onChange={(e) => setPostedAt(e.currentTarget.value)}
                required
              />
            </Field>
            <Field label="Description" required>
              <Input
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                placeholder="Owner contribution, bank fee refund, etc."
                required
                style={{ minWidth: 320 }}
              />
            </Field>
          </div>

          <table className="kb-report" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Account</th>
                <th className="kb-report__amt">Debit ($)</th>
                <th className="kb-report__amt">Credit ($)</th>
                <th>Memo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td style={{ minWidth: 260 }}>
                    <Combobox
                      options={accountOptions}
                      value={l.accountId}
                      onChange={(v) => patchLine(i, { accountId: v })}
                      placeholder="Search by code or name…"
                      width="100%"
                    />
                  </td>
                  <td>
                    <Input
                      value={l.debit}
                      // Debit and credit are mutually exclusive per line.
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        patchLine(i, v ? { debit: v, credit: '' } : { debit: v });
                      }}
                      placeholder="0.00"
                      inputMode="decimal"
                      style={amtInput}
                    />
                  </td>
                  <td>
                    <Input
                      value={l.credit}
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        patchLine(i, v ? { credit: v, debit: '' } : { credit: v });
                      }}
                      placeholder="0.00"
                      inputMode="decimal"
                      style={amtInput}
                    />
                  </td>
                  <td>
                    <Input
                      value={l.memo}
                      onChange={(e) => patchLine(i, { memo: e.currentTarget.value })}
                      placeholder="(optional)"
                    />
                  </td>
                  <td>
                    {lines.length > 2 && (
                      <button type="button" className="kb-linkbtn" onClick={() => removeLine(i)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className={balanced ? 'kb-report__subtotal' : ''}>
                <td>
                  <button type="button" className="kb-linkbtn" onClick={addLine}>
                    + Add line
                  </button>
                </td>
                <td className="kb-report__amt">{fmt(sumDebits)}</td>
                <td className="kb-report__amt">{fmt(sumCredits)}</td>
                <td
                  colSpan={2}
                  style={{ color: balanced ? 'var(--kb-green-600)' : 'var(--kb-red-600)' }}
                >
                  {balanced ? 'Balanced' : `Off by ${fmt(Math.abs(offBy))}`}
                </td>
              </tr>
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button
              type="submit"
              disabled={busy || !balanced || !allHaveAccounts || description.trim().length === 0}
            >
              {busy ? 'Posting…' : 'Post entry'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
